/**
 * RequestorDetailModal — the Job Board's Requestor column popup (ClickUp 86bazkeyn).
 *
 * READ-ONLY, deliberately. The ticket asked for inline editing gated by permission, and that
 * turned out to be the blocker: no endpoint edits another person's record, and `MANAGE_USERS`
 * today gates device/PIN/roster actions only — reusing it here would silently widen what that
 * permission means everywhere else it is already checked. Editing waits for a permission model
 * decided on its own terms rather than one invented inside a job-row popup.
 *
 * Note what this popup shows and what it does NOT: the REQUESTOR is the customer contact who
 * called the load in. The board's "Created by" column is the DISPATCHER who keyed the ticket
 * in — a different person, a `users` row, and not linked from here. Those two were conflated
 * under a single "Requestor" label until OPEN-053 renamed one of them.
 *
 * Body shared with the Sites & Operators detail pane via `RequestorSummary`.
 */
import * as React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/components/ui/dialog';
import { useRequestors } from './hooks';
import { RequestorSummary } from './RequestorSummary';
import { subText } from '../sites-operators/ui-bits';

export interface RequestorDetailModalProps {
  /** null = closed. */
  readonly requestorId: string | null;
  /** The name off the job row — shown while the record loads, and if it cannot be found. */
  readonly fallbackName?: string | null;
  readonly onClose: () => void;
}

export function RequestorDetailModal({
  requestorId,
  fallbackName,
  onClose,
}: RequestorDetailModalProps): React.JSX.Element {
  // The full list is already cached by the requestors screens and is small; looking the record
  // up from it avoids a second per-id endpoint. Deactivated people are included — a ticket can
  // reference someone who has since left, and that must still open.
  const { data, isLoading } = useRequestors();
  const requestor = data?.requestors.find((r) => r.id === requestorId) ?? null;

  return (
    <Dialog
      open={requestorId !== null}
      onOpenChange={(open): void => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Requestor</DialogTitle>
        </DialogHeader>

        {requestor !== null ? (
          <RequestorSummary requestor={requestor} />
        ) : (
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 700 }}>{fallbackName ?? 'Requestor'}</h2>
            <p style={subText}>
              {isLoading
                ? 'Loading…'
                : // Not an error state worth alarming anyone with: the job holds a snapshot of
                  // the name, so the ticket still reads correctly even when the record behind
                  // it is gone.
                  'This requestor is no longer in the catalog. The ticket keeps the name it was created with.'}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
